import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useFileProcessing } from './useFileProcessing'
import { useOptimisticUpdates } from './useOptimisticUpdates'

// Create a stable ID for each file row
const getFileId = (file) => {
  return file.ont_bam_id || file.pacbio_bam_id || file.vcf_id || file.fastq_pair_id || file.fastq_file_id || file.id
}

// Deep comparison for individual file properties that affect UI
const compareFileProperties = (prevFile, newFile) => {
  if (!prevFile || !newFile) return false

  // Compare key properties that would affect UI rendering
  const keysToCompare = [
    'name',
    'sample_name',
    'is_annotated',
    'is_finished',
    'created_at',
    'is_refunded',
    'user_notified',
    'analysis_report_exists',
    'ai_annotation',
    'sarek_run_type',
    'sarek_bed_name',
    'original_reference',
    'notes',
    'file_notes',
  ]

  // Check simple properties
  const simplePropsChanged = keysToCompare.some((key) => {
    const prevValue = prevFile[key]
    const newValue = newFile[key]

    // Handle objects and arrays with JSON comparison
    if (typeof prevValue === 'object' && typeof newValue === 'object') {
      return JSON.stringify(prevValue) !== JSON.stringify(newValue)
    }

    return prevValue !== newValue
  })

  if (simplePropsChanged) return false

  // Compare nested analysis and annotation status fields for status changes
  const prevAnalysesStatus = prevFile.analyses?.status
  const newAnalysesStatus = newFile.analyses?.status
  if (prevAnalysesStatus !== newAnalysesStatus) {
    return false // Status changed - trigger update
  }

  // Compare annotations status (for FASTQ files)
  const prevAnnotationsStatus = prevFile.annotations?.status
  const newAnnotationsStatus = newFile.annotations?.status
  if (prevAnnotationsStatus !== newAnnotationsStatus) {
    return false // Annotation status changed - trigger update
  }

  // Compare analysis list length (new analyses added)
  const prevAnalysesList = prevFile.analyses?.list || []
  const newAnalysesList = newFile.analyses?.list || []
  if (prevAnalysesList.length !== newAnalysesList.length) {
    return false // Analysis list changed - trigger update
  }

  // Compare individual analysis statuses within the list
  for (let i = 0; i < prevAnalysesList.length; i++) {
    if (prevAnalysesList[i]?.status !== newAnalysesList[i]?.status) {
      return false // Individual analysis status changed - trigger update
    }
  }

  return true // No changes detected
}

// Detect changes between two data sets
const detectDataChanges = (prevData, newData) => {
  const prevMap = new Map(prevData.map((file) => [getFileId(file), file]))
  const newMap = new Map(newData.map((file) => [getFileId(file), file]))

  const additions = []
  const updates = []
  const deletions = []
  const unchanged = []

  // Check for additions and updates
  newData.forEach((newFile) => {
    const fileId = getFileId(newFile)
    const prevFile = prevMap.get(fileId)

    if (!prevFile) {
      additions.push(newFile)
    } else if (!compareFileProperties(prevFile, newFile)) {
      updates.push({ prev: prevFile, new: newFile, id: fileId })
    } else {
      unchanged.push(newFile)
    }
  })

  // Check for deletions
  prevData.forEach((prevFile) => {
    const fileId = getFileId(prevFile)
    if (!newMap.has(fileId)) {
      deletions.push(prevFile)
    }
  })

  return {
    additions,
    updates,
    deletions,
    unchanged,
    hasChanges: additions.length > 0 || updates.length > 0 || deletions.length > 0,
  }
}

export const useStableFilesData = (filesQuery) => {
  const { status, data: rawData = [] } = filesQuery
  const [stableData, setStableData] = useState([])
  const [isUpdating, setIsUpdating] = useState(false)
  const [updateType, setUpdateType] = useState(null) // 'addition', 'update', 'deletion', 'mixed'
  const [lastUpdateInfo, setLastUpdateInfo] = useState(null)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [itemTransitions, setItemTransitions] = useState({
    newItems: new Set(),
    deletingItems: new Set(),
    updatedItems: new Set(),
  })
  const previousDataRef = useRef([])
  const isInitializedRef = useRef(false)
  const transitionTimeoutRef = useRef(null)

  // Optimistic updates
  const optimisticUpdates = useOptimisticUpdates()

  // Process the data
  const processedData = rawData

  // Apply optimistic updates to the processed data
  const optimisticData = useMemo(() => {
    // First filter out files that are being deleted
    const filteredData = optimisticUpdates.filterDeletingFiles(processedData)
    // Then add any uploading files as temporary entries
    return optimisticUpdates.addUploadingToData(filteredData)
  }, [processedData, optimisticUpdates])

  // Memoize processed data to prevent unnecessary recalculations
  const memoizedProcessedData = useMemo(() => optimisticData, [optimisticData])

  // Clear transition states after animations complete
  const clearTransitionStates = useCallback(() => {
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current)
    }

    transitionTimeoutRef.current = setTimeout(() => {
      setItemTransitions({
        newItems: new Set(),
        deletingItems: new Set(),
        updatedItems: new Set(),
      })
    }, 500) // Clear time for smooth status transitions
  }, [])

  // Smooth update function that preserves existing data and adds new items
  const performSmoothUpdate = useCallback(
    (changes, newProcessedData) => {
      const { additions, updates, deletions } = changes

      // Determine update type for UI feedback - ignore deletions for simplicity
      let updateTypeStr = null
      if (additions.length > 0 && updates.length === 0) {
        updateTypeStr = 'addition'
      } else if (updates.length > 0 && additions.length === 0) {
        updateTypeStr = 'update'
      } else if (deletions.length > 0 && additions.length === 0 && updates.length === 0) {
        updateTypeStr = 'deletion' // Will be handled silently
      } else if (additions.length > 0 || updates.length > 0) {
        updateTypeStr = 'mixed' // Only show mixed if there are additions/updates
      } else {
        updateTypeStr = 'deletion' // Pure deletion - silent
      }

      // Update transition state to mark items for animation
      setItemTransitions((prev) => ({
        newItems: new Set([...prev.newItems, ...additions.map(getFileId)]),
        deletingItems: new Set([...prev.deletingItems, ...deletions.map(getFileId)]),
        updatedItems: new Set([...prev.updatedItems, ...updates.map((u) => u.id)]),
      }))

      setUpdateType(updateTypeStr)
      setIsUpdating(true)

      // Store update info for notifications
      setLastUpdateInfo({
        additionCount: additions.length,
        updateCount: updates.length,
        deletionCount: deletions.length,
      })

      // For additions only, smooth integration without UI disruption
      if (updateTypeStr === 'addition') {
        setTimeout(() => {
          setStableData((prevData) => {
            const existingIds = new Set(prevData.map(getFileId))
            const newItems = additions.filter((item) => !existingIds.has(getFileId(item)))
            return [...prevData, ...newItems]
          })
          setIsUpdating(false)
          setUpdateType(null)
          clearTransitionStates()
        }, 50) // Very fast for smooth additions
      } else if (updateTypeStr === 'deletion') {
        // For deletions - completely silent, no loading states
        setIsUpdating(false)
        setUpdateType(null)
        setTimeout(() => {
          setStableData(newProcessedData)
          setTimeout(() => {
            clearTransitionStates()
          }, 600) // Time for graceful fade-out completion
        }, 100)
      } else {
        // For updates/mixed - faster timing for smooth status transitions
        setTimeout(() => {
          setStableData(newProcessedData)
          setIsUpdating(false)
          setUpdateType(null)
          clearTransitionStates()
        }, 200) // Faster updates for status changes
      }
    },
    [clearTransitionStates],
  )

  useEffect(() => {
    if (status === 'success') {
      // Initial load
      if (!isInitializedRef.current && memoizedProcessedData.length > 0) {
        setStableData(memoizedProcessedData)
        previousDataRef.current = rawData
        isInitializedRef.current = true
        setIsInitialLoad(false) // Mark initial load as complete
        return
      }

      // Check for changes after initial load
      if (isInitializedRef.current) {
        const changes = detectDataChanges(previousDataRef.current, rawData)

        if (changes.hasChanges) {
          performSmoothUpdate(changes, memoizedProcessedData)
          previousDataRef.current = rawData
        } else if (isUpdating) {
          // Data hasn't changed but we're still showing updating - clear it
          setIsUpdating(false)
          setUpdateType(null)
        }
      }
    }

    // Handle loading states - but with timeout to prevent getting stuck
    if (status === 'loading' && isInitializedRef.current && !isUpdating) {
      // This is a refresh/update, not initial load - show minimal overlay
      setIsUpdating(true)
      setUpdateType('mixed')
    }
  }, [status, rawData, memoizedProcessedData, performSmoothUpdate, isUpdating])

  // Watchdog effect to prevent stuck updating state
  useEffect(() => {
    if (isUpdating) {
      const watchdogTimeout = setTimeout(() => {
        // Silent recovery
        setIsUpdating(false)
        setUpdateType(null)
      }, 3000) // Clear after 3 seconds max

      return () => clearTimeout(watchdogTimeout)
    }
  }, [isUpdating])

  return {
    data: stableData,
    isInitialLoading: status === 'loading' && isInitialLoad,
    isUpdating,
    updateType, // 'addition', 'update', 'deletion', 'mixed'
    lastUpdateInfo, // { additionCount, updateCount, deletionCount }
    isSuccess: status === 'success',
    hasData: stableData.length > 0,

    // Transition state tracking for smooth animations
    itemTransitions,

    // Optimistic updates
    optimisticUpdates,

    // Helper methods
    refresh: useCallback(() => {
      if (filesQuery.refresh) {
        filesQuery.refresh()
      }
    }, [filesQuery]),

    clearLastUpdateInfo: useCallback(() => {
      setLastUpdateInfo(null)
    }, []),

    // Force clear updating state (useful for recovery)
    forceStopUpdating: useCallback(() => {
      setIsUpdating(false)
      setUpdateType(null)
      setLastUpdateInfo(null)
      setItemTransitions({
        newItems: new Set(),
        deletingItems: new Set(),
        updatedItems: new Set(),
      })
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current)
      }
    }, []),
  }
}
